import {
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Switch,
  Text,
  Textarea,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, EditIcon, ViewIcon } from '@chakra-ui/icons';
import { createColumnHelper } from '@tanstack/react-table';
import {
  createMonitorJobApi,
  deleteMonitorJobApi,
  getMonitorJobLogsApi,
  getMonitorJobsApi,
  getMonitorTypesApi,
  toggleMonitorJobApi,
  updateMonitorJobApi,
} from 'api/monitorApi';
import { getAllCustomersApi } from 'api/adminApi';
import Card from 'components/card/Card';
import DataTable from 'components/DataTable';
import { HelpLink } from 'components/HelpLink';
import SelectInput from 'components/inputs/SelectInput';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  buildAdditionalDataForSave,
  buildCustomerNameById,
  displayCustomerForJob,
  formatMonitorCustomerOptionLabel,
  isMonitorJobFormValid,
  mergeCustomerIntoAdditionalData,
  prepareMonitorJobsForTable,
  resolveJobCustomerId,
} from './monitoringJobsHelpers';
import { nextMonitorJobsTableKey } from './monitoringJobsTableHelpers';

// Must match the string values produced by fetcher-event-contract's FetcherType/FinanceType
// and normalizer-event-contract's SourceType, prefixed the same way monitor-service writes
// them to the events table ("fetch|<type>" / "normalize|<type>").
const EVENT_TYPE_GROUPS = [
  {
    label: 'Fetch',
    options: [
      { value: 'fetch|shopify_partner', label: 'Shopify Partner' },
      { value: 'fetch|shopify_store', label: 'Shopify Store' },
      { value: 'fetch|heron', label: 'Heron' },
      { value: 'fetch|plaid', label: 'Plaid' },
      { value: 'fetch|lender_idb', label: 'Lender IDB' },
      { value: 'fetch|lender_mercury', label: 'Lender Mercury' },
      { value: 'fetch|term_sofr', label: 'Term SOFR' },
    ],
  },
  {
    label: 'Normalize',
    options: [
      { value: 'normalize|google_play', label: 'Google Play' },
      { value: 'normalize|app_store', label: 'App Store' },
      { value: 'normalize|shopify_store', label: 'Shopify Store' },
      { value: 'normalize|shopify_partner', label: 'Shopify Partner' },
      { value: 'normalize|stripe', label: 'Stripe' },
      { value: 'normalize|mixpanel', label: 'Mixpanel' },
      { value: 'normalize|amplitude', label: 'Amplitude' },
      { value: 'normalize|amazon_selling_partner', label: 'Amazon Selling Partner' },
      { value: 'normalize|google_ads', label: 'Google Ads' },
      { value: 'normalize|facebook_ads', label: 'Facebook Ads' },
      { value: 'normalize|amazon_ads', label: 'Amazon Ads' },
      { value: 'normalize|apple_search_ads', label: 'Apple Search Ads' },
      { value: 'normalize|heron', label: 'Heron' },
      { value: 'normalize|plaid', label: 'Plaid' },
      { value: 'normalize|tiktok', label: 'TikTok' },
      { value: 'normalize|braintree', label: 'Braintree' },
      { value: 'normalize|woocommerce', label: 'WooCommerce' },
      { value: 'normalize|applovin', label: 'AppLovin' },
    ],
  },
];

const EVENT_TYPE_VALUES = new Set(EVENT_TYPE_GROUPS.flatMap((g) => g.options.map((o) => o.value)));

const DAYS_LABEL = {
  '0': 'Sun',
  '1': 'Mon',
  '2': 'Tue',
  '3': 'Wed',
  '4': 'Thu',
  '5': 'Fri',
  '6': 'Sat',
};

const formatSchedule = (cronHour, cronDays) => {
  const hour = cronHour ? `${cronHour.padStart(2, '0')}:00 UTC` : '-';
  if (!cronDays) return hour;

  let daysLabel;
  if (cronDays === '*' || cronDays === '0-6') {
    daysLabel = 'Every day';
  } else if (cronDays.includes(',')) {
    daysLabel = cronDays
      .split(',')
      .map((d) => DAYS_LABEL[d.trim()] || d.trim())
      .join(', ');
  } else if (cronDays.includes('-')) {
    const [start, end] = cronDays.split('-');
    daysLabel = `${DAYS_LABEL[start.trim()] || start.trim()}–${DAYS_LABEL[end.trim()] || end.trim()}`;
  } else {
    daysLabel = DAYS_LABEL[cronDays.trim()] || cronDays;
  }

  return `${daysLabel} at ${hour}`;
};

const emptyForm = {
  monitorTypeId: '',
  eventType: '',
  customerId: '',
  cronHourInUtc: '',
  cronDaysOfWeek: '',
  additionalData: '',
  isEnabled: true,
};

const DataMonitoring = () => {
  const [jobs, setJobs] = useState([]);
  const [monitorTypes, setMonitorTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingJob, setEditingJob] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsJobId, setLogsJobId] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [isCustomEventType, setIsCustomEventType] = useState(false);
  const [tableKey, setTableKey] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const customerNameById = useMemo(
    () => buildCustomerNameById(customers),
    [customers]
  );

  const customerOptions = useMemo(
    () =>
      [...customers]
        .sort((a, b) => (a.companyName || '').localeCompare(b.companyName || ''))
        .map((c) => ({
          value: c.id,
          label: formatMonitorCustomerOptionLabel(c.companyName, c.id),
        })),
    [customers]
  );

  const tableJobs = useMemo(
    () => prepareMonitorJobsForTable(jobs, searchQuery, customerNameById),
    [jobs, searchQuery, customerNameById]
  );

  const customerFormOptions = useMemo(
    () => [{ value: '', label: 'All customers' }, ...customerOptions],
    [customerOptions]
  );

  const { isOpen: isFormOpen, onOpen: onFormOpen, onClose: onFormClose } = useDisclosure();
  const { isOpen: isLogsOpen, onOpen: onLogsOpen, onClose: onLogsClose } = useDisclosure();
  const toast = useToast();
  const columnHelper = createColumnHelper();

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getMonitorJobsApi();
      setJobs(data || []);
    } catch {
      toast({ title: 'Failed to load jobs', status: 'error', duration: 4000 });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const handleRefresh = useCallback(() => {
    setTableKey((key) => nextMonitorJobsTableKey(key));
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    fetchJobs();
    getMonitorTypesApi()
      .then(setMonitorTypes)
      .catch(() => {});
    getAllCustomersApi()
      .then((data) => setCustomers(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [fetchJobs]);

  const openCreate = () => {
    setEditingJob(null);
    setForm(emptyForm);
    setIsCustomEventType(false);
    onFormOpen();
  };

  const openEdit = (job) => {
    setEditingJob(job);
    const customerId = resolveJobCustomerId(job);
    setForm({
      monitorTypeId: String(job.monitorTypeId),
      eventType: job.eventType || '',
      customerId,
      cronHourInUtc: job.cronHourInUtc || '',
      cronDaysOfWeek: job.cronDaysOfWeek || '',
      additionalData: job.additionalData
        ? JSON.stringify(job.additionalData, null, 2)
        : '',
      isEnabled: job.isEnabled,
    });
    setIsCustomEventType(!!job.eventType && !EVENT_TYPE_VALUES.has(job.eventType));
    onFormOpen();
  };

  const handleCustomerChange = (customerId) => {
    const id = customerId || '';
    const customerName = id ? customerNameById[id] || '' : '';
    setForm((prev) => {
      let additionalData = prev.additionalData;
      try {
        const parsed = prev.additionalData.trim()
          ? JSON.parse(prev.additionalData)
          : null;
        const merged = mergeCustomerIntoAdditionalData(parsed, {
          customerId: id,
          customerName,
          omitCustomerIdFromJson: true,
        });
        additionalData = merged ? JSON.stringify(merged, null, 2) : '';
      } catch {
        // keep textarea unchanged if JSON is invalid
      }
      return { ...prev, customerId: id, additionalData };
    });
  };

  const openLogs = async (job) => {
    setLogsJobId(job.id);
    setLogs([]);
    onLogsOpen();
    setLogsLoading(true);
    try {
      const data = await getMonitorJobLogsApi({ jobId: job.id });
      setLogs(data || []);
    } catch {
      toast({ title: 'Failed to load logs', status: 'error', duration: 4000 });
    } finally {
      setLogsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!isFormValid) return;
    setSaving(true);
    try {
      const customerName = form.customerId
        ? customerNameById[form.customerId] || ''
        : '';
      const additionalData = buildAdditionalDataForSave(form.additionalData, {
        customerId: form.customerId,
        customerName,
      });

      const payload = {
        monitorTypeId: parseInt(form.monitorTypeId, 10),
        eventType: form.eventType,
        customerId: form.customerId || null,
        cronHourInUtc: form.cronHourInUtc,
        cronDaysOfWeek: form.cronDaysOfWeek,
        additionalData,
        isEnabled: form.isEnabled,
      };

      if (editingJob) {
        await updateMonitorJobApi({ jobId: editingJob.id, ...payload });
        toast({ title: 'Job updated', status: 'success', duration: 3000 });
      } else {
        await createMonitorJobApi(payload);
        toast({ title: 'Job created', status: 'success', duration: 3000 });
      }

      onFormClose();
      fetchJobs();
    } catch (e) {
      toast({
        title: 'Error',
        description: e?.message || 'Invalid JSON in Additional Data or save failed',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm('Delete this monitoring job?')) return;
    setDeletingId(jobId);
    try {
      await deleteMonitorJobApi({ jobId });
      toast({ title: 'Job deleted', status: 'success', duration: 3000 });
      fetchJobs();
    } catch {
      toast({ title: 'Failed to delete job', status: 'error', duration: 4000 });
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggle = async (job) => {
    try {
      await toggleMonitorJobApi({ jobId: job.id, isEnabled: !job.isEnabled });
      fetchJobs();
    } catch {
      toast({ title: 'Failed to toggle job', status: 'error', duration: 4000 });
    }
  };

  const isFormValid = isMonitorJobFormValid(form);

  const columns = useMemo(
    () => [
    columnHelper.accessor('id', {
      header: 'ID',
      cell: (info) => <Text fontSize="sm" fontFamily="mono">{info.getValue()}</Text>,
    }),
    columnHelper.accessor('eventType', {
      header: 'Event Type',
      cell: (info) => <Text fontSize="sm" fontFamily="mono">{info.getValue() || '-'}</Text>,
    }),
    columnHelper.accessor('monitorType', {
      header: 'Monitor Type',
      cell: (info) => {
        const type = info.getValue();
        const colors = { by_day: 'blue', any_day: 'purple', bq_threshold: 'teal' };
        return (
          <Badge colorScheme={colors[type] || 'gray'} fontSize="xs">
            {type}
          </Badge>
        );
      },
    }),
    columnHelper.accessor('customerId', {
      header: 'Customer',
      cell: (info) => {
        const { label, tooltip } = displayCustomerForJob(
          info.row.original,
          customerNameById
        );
        return tooltip ? (
          <Tooltip label={tooltip}>
            <Text fontSize="sm">{label}</Text>
          </Tooltip>
        ) : (
          <Text fontSize="sm" color="gray.400">
            {label}
          </Text>
        );
      },
    }),
    columnHelper.display({
      id: 'schedule',
      header: 'Schedule',
      cell: (info) => {
        const { cronHourInUtc, cronDaysOfWeek } = info.row.original;
        return (
          <Text fontSize="sm">{formatSchedule(cronHourInUtc, cronDaysOfWeek)}</Text>
        );
      },
    }),
    columnHelper.accessor('isEnabled', {
      header: 'Enabled',
      cell: (info) => {
        const job = info.row.original;
        return (
          <Switch
            size="sm"
            isChecked={info.getValue()}
            onChange={() => handleToggle(job)}
          />
        );
      },
    }),
    columnHelper.display({
      id: 'lastRun',
      header: 'Last Run',
      cell: (info) => {
        const log = info.row.original.latestLog;
        if (!log) return <Text fontSize="sm" color="gray.400">Never</Text>;
        return (
          <Text fontSize="sm">
            {moment(log.createdAt).format('MMM DD, HH:mm')}
          </Text>
        );
      },
    }),
    columnHelper.display({
      id: 'lastResult',
      header: 'Result',
      cell: (info) => {
        const log = info.row.original.latestLog;
        if (!log) return <Text fontSize="sm" color="gray.400">-</Text>;
        return (
          <Badge colorScheme={log.isOk ? 'green' : 'red'} fontSize="xs">
            {log.isOk ? 'OK' : 'Failed'}
          </Badge>
        );
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: (info) => {
        const job = info.row.original;
        return (
          <HStack spacing={1}>
            <Tooltip label="View logs">
              <IconButton
                icon={<ViewIcon />}
                size="xs"
                variant="ghost"
                onClick={() => openLogs(job)}
                aria-label="View logs"
              />
            </Tooltip>
            <Tooltip label="Edit">
              <IconButton
                icon={<EditIcon />}
                size="xs"
                variant="ghost"
                onClick={() => openEdit(job)}
                aria-label="Edit job"
              />
            </Tooltip>
            <Tooltip label="Delete">
              <IconButton
                icon={<DeleteIcon />}
                size="xs"
                variant="ghost"
                colorScheme="red"
                isLoading={deletingId === job.id}
                onClick={() => handleDelete(job.id)}
                aria-label="Delete job"
              />
            </Tooltip>
          </HStack>
        );
      },
    }),
  ],
    [columnHelper, customerNameById, deletingId]
  );

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <HStack spacing={3} align="center">
          <Heading size="lg">Monitor Jobs</Heading>
          <HelpLink
            sectionId="data-monitoring-overview"
            variant="subtle"
            fontSize="sm"
          >
            How to set up monitors
          </HelpLink>
        </HStack>
        <HStack spacing={3}>
          <Button size="sm" onClick={handleRefresh} isLoading={loading}>
            Refresh
          </Button>
          <Button
            size="sm"
            colorScheme="blue"
            leftIcon={<AddIcon />}
            onClick={openCreate}
          >
            New Job
          </Button>
        </HStack>
      </Flex>

      <Card>
        {loading && jobs.length === 0 ? (
          <Flex justify="center" align="center" minH="200px">
            <Spinner size="lg" />
          </Flex>
        ) : jobs.length === 0 ? (
          <Flex justify="center" align="center" minH="200px">
            <Text color="gray.500">No monitoring jobs found</Text>
          </Flex>
        ) : (
          <Box mx={-5}>
            <Box px={5} pb={3}>
              <FormControl maxW={{ base: '100%', md: '320px' }}>
                <FormLabel fontSize="sm" mb={1}>
                  Search jobs
                </FormLabel>
                <Input
                  size="sm"
                  placeholder="Job id, customer, or event"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </FormControl>
            </Box>
            {tableJobs.length === 0 ? (
              <Flex justify="center" align="center" minH="120px" px={5}>
                <Text color="gray.500">No jobs match your search</Text>
              </Flex>
            ) : (
              <DataTable
                key={tableKey}
                data={tableJobs}
                columns={columns}
                hasPagination={true}
                autoResetPageIndex={false}
              />
            )}
          </Box>
        )}
      </Card>

      {/* Create / Edit Modal */}
      <Modal isOpen={isFormOpen} onClose={onFormClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingJob ? 'Edit Monitor Job' : 'New Monitor Job'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel fontSize="sm">Monitor Type</FormLabel>
                <Select
                  size="sm"
                  placeholder="Select type"
                  value={form.monitorTypeId}
                  onChange={(e) => setForm({ ...form, monitorTypeId: e.target.value })}
                >
                  {monitorTypes.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Event Type</FormLabel>
                {isCustomEventType ? (
                  <Input
                    size="sm"
                    fontFamily="mono"
                    placeholder="e.g. fetch|my_custom_type"
                    value={form.eventType}
                    onChange={(e) => setForm({ ...form, eventType: e.target.value })}
                  />
                ) : (
                  <Select
                    size="sm"
                    placeholder="Select event type"
                    value={form.eventType}
                    onChange={(e) => setForm({ ...form, eventType: e.target.value })}
                  >
                    {form.eventType && !EVENT_TYPE_VALUES.has(form.eventType) && (
                      <option value={form.eventType}>{`Current: ${form.eventType} (legacy)`}</option>
                    )}
                    {EVENT_TYPE_GROUPS.map((group) => (
                      <optgroup key={group.label} label={group.label}>
                        {group.options.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </Select>
                )}
                <Button
                  size="xs"
                  variant="link"
                  mt={1}
                  onClick={() => {
                    if (!isCustomEventType) setForm({ ...form, eventType: '' });
                    setIsCustomEventType(!isCustomEventType);
                  }}
                >
                  {isCustomEventType ? 'Choose from list instead' : 'Enter custom value instead'}
                </Button>
                {form.eventType && (
                  <Text fontSize="xs" color="gray.500" mt={1} fontFamily="mono">
                    Will be saved as: {form.eventType}
                  </Text>
                )}
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">
                  Customer{' '}
                  <Text as="span" color="gray.400" fontSize="xs">
                    (optional — All customers when blank)
                  </Text>
                </FormLabel>
                <SelectInput
                  enableSearch
                  isClearable
                  placeholder="All customers"
                  options={customerFormOptions}
                  value={form.customerId || null}
                  onChange={handleCustomerChange}
                />
              </FormControl>

              <HStack spacing={4} align="flex-start">
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Cron Hour (UTC)</FormLabel>
                  <Input
                    size="sm"
                    placeholder="e.g. 14"
                    value={form.cronHourInUtc}
                    onChange={(e) => setForm({ ...form, cronHourInUtc: e.target.value })}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm">Cron Days of Week</FormLabel>
                  <Input
                    size="sm"
                    placeholder="e.g. 1-5 or 0,3,5"
                    value={form.cronDaysOfWeek}
                    onChange={(e) => setForm({ ...form, cronDaysOfWeek: e.target.value })}
                  />
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel fontSize="sm">
                  Additional Data{' '}
                  <Text as="span" color="gray.400" fontSize="xs">(JSON — required for BQ threshold)</Text>
                  {' '}
                  <HelpLink
                    sectionId="data-monitoring-bq-threshold"
                    variant="subtle"
                    fontSize="xs"
                  >
                    BQ JSON example
                  </HelpLink>
                </FormLabel>
                <Textarea
                  size="sm"
                  fontFamily="mono"
                  fontSize="xs"
                  rows={4}
                  placeholder='{"procedure":"dataset.proc","customerName":"Acme Co","threshold":{"operator":">=","value":1500000}}'
                  value={form.additionalData}
                  onChange={(e) => setForm({ ...form, additionalData: e.target.value })}
                />
              </FormControl>

              <FormControl>
                <HStack>
                  <Switch
                    isChecked={form.isEnabled}
                    onChange={(e) => setForm({ ...form, isEnabled: e.target.checked })}
                  />
                  <FormLabel mb={0} fontSize="sm">Enabled</FormLabel>
                </HStack>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button size="sm" variant="ghost" onClick={onFormClose}>
                Cancel
              </Button>
              <Button
                size="sm"
                colorScheme="blue"
                isLoading={saving}
                isDisabled={!isFormValid}
                onClick={handleSave}
              >
                {editingJob ? 'Save Changes' : 'Create Job'}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Logs Drawer */}
      <Drawer isOpen={isLogsOpen} onClose={onLogsClose} placement="right" size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Recent Logs — Job #{logsJobId}</DrawerHeader>
          <DrawerBody>
            {logsLoading ? (
              <Flex justify="center" align="center" minH="200px">
                <Spinner />
              </Flex>
            ) : logs.length === 0 ? (
              <Text color="gray.500">No logs found for this job.</Text>
            ) : (
              <VStack spacing={3} align="stretch">
                {logs.map((log) => (
                  <Box
                    key={log.taskId}
                    p={3}
                    borderRadius="md"
                    borderLeft="4px solid"
                    borderLeftColor={log.isOk ? 'green.400' : 'red.400'}
                    bg={log.isOk ? 'green.50' : 'red.50'}
                    _dark={{ bg: log.isOk ? 'green.900' : 'red.900' }}
                  >
                    <HStack justify="space-between" mb={1}>
                      <Badge colorScheme={log.isOk ? 'green' : 'red'} fontSize="xs">
                        {log.isOk ? 'OK' : 'Failed'}
                      </Badge>
                      <Text fontSize="xs" color="gray.500">
                        {moment(log.createdAt).format('MMM DD, YYYY HH:mm:ss')}
                      </Text>
                    </HStack>
                    <Text fontSize="xs" fontFamily="mono" color="gray.600">
                      Execution: {moment(log.executionTime).format('MMM DD, YYYY')}
                    </Text>
                    <Text fontSize="xs" fontFamily="mono" color="gray.400" mt={1}>
                      {log.taskId}
                    </Text>
                  </Box>
                ))}
              </VStack>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default DataMonitoring;
